/**
 * Shifts catalog (M17.9) at /policies/shifts. Lists active shifts for a
 * legal entity (?le=<uuid>) with create + deactivate.
 *
 * The backend Shift schema lands in M17.1; this page consumes it.
 * Shifts are policy-level (work-schedule rules) so the page lives
 * under the Policies module per the locked 7-module IA.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock, Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { EmptyState, EntityDrawer, PageHeader } from "../components/ui";
import { api, ApiError } from "../lib/api";
import type { ShiftDetail } from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";

function NewShiftDialog({
  legalEntityId,
  onClose,
  onCreated,
}: {
  legalEntityId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    code: "",
    name_en: "",
    name_ar: "",
    start_time: "08:00",
    end_time: "17:00",
    grace_period_minutes: 10,
    break_duration_minutes: 60,
  });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () =>
      api.createShift({ legal_entity_id: legalEntityId, ...form }),
    onSuccess: () => onCreated(),
    onError: (err) =>
      setErrorMsg(err instanceof ApiError ? err.detail : String(err)),
  });
  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("policies.shifts_new")}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            form="new-shift-form"
            disabled={m.isPending}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg hover:bg-brand-hover disabled:opacity-50"
          >
            {m.isPending
              ? t("policies.creating")
              : t("policies.create")}
          </button>
        </div>
      }
    >
        <form
          id="new-shift-form"
          onSubmit={(e) => {
            e.preventDefault();
            setErrorMsg(null);
            m.mutate();
          }}
          className="flex flex-col gap-3 text-sm"
        >
          <div className="grid grid-cols-2 gap-3">
            <label>
              <span className="text-xs font-medium uppercase text-text-secondary">{t("policies.code_label")}</span>
              <input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                className="mt-1 w-full rounded border border-border-default px-3 py-2"
                required
              />
            </label>
            <label>
              <span className="text-xs font-medium uppercase text-text-secondary">{t("policies.name_en_label")}</span>
              <input
                value={form.name_en}
                onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                className="mt-1 w-full rounded border border-border-default px-3 py-2"
                required
              />
            </label>
            <label>
              <span className="text-xs font-medium uppercase text-text-secondary">{t("policies.name_ar_label")}</span>
              <input
                value={form.name_ar}
                onChange={(e) => setForm({ ...form, name_ar: e.target.value })}
                dir="rtl"
                className="mt-1 w-full rounded border border-border-default px-3 py-2"
                required
              />
            </label>
            <label>
              <span className="text-xs font-medium uppercase text-text-secondary">{t("policies.shifts_start_short")}</span>
              <input
                type="time"
                value={form.start_time}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                className="mt-1 w-full rounded border border-border-default px-3 py-2"
                required
              />
            </label>
            <label>
              <span className="text-xs font-medium uppercase text-text-secondary">{t("policies.shifts_end_short")}</span>
              <input
                type="time"
                value={form.end_time}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                className="mt-1 w-full rounded border border-border-default px-3 py-2"
                required
              />
            </label>
            <label>
              <span className="text-xs font-medium uppercase text-text-secondary">{t("policies.shifts_grace_short")}</span>
              <input
                type="number"
                min={0}
                max={120}
                value={form.grace_period_minutes}
                onChange={(e) =>
                  setForm({ ...form, grace_period_minutes: parseInt(e.target.value, 10) })
                }
                className="mt-1 w-full rounded border border-border-default px-3 py-2"
              />
            </label>
            <label>
              <span className="text-xs font-medium uppercase text-text-secondary">{t("policies.shifts_break_short")}</span>
              <input
                type="number"
                min={0}
                max={240}
                value={form.break_duration_minutes}
                onChange={(e) =>
                  setForm({ ...form, break_duration_minutes: parseInt(e.target.value, 10) })
                }
                className="mt-1 w-full rounded border border-border-default px-3 py-2"
              />
            </label>
          </div>
          {errorMsg && (
            <p className="rounded bg-rose-50 p-2 text-rose-700">{errorMsg}</p>
          )}
        </form>
    </EntityDrawer>
  );
}

function ShiftRow({ shift, onChanged }: { shift: ShiftDetail; onChanged: () => void }) {
  const { t } = useTranslation();
  const deactivateM = useMutation({
    mutationFn: () => api.updateShift(shift.id, { is_active: false }),
    onSuccess: onChanged,
  });
  const deleteM = useMutation({
    mutationFn: () => api.deleteShift(shift.id),
    onSuccess: onChanged,
  });
  const days = [
    t("operations.leave_cal_day_sun"),
    t("operations.leave_cal_day_mon"),
    t("operations.leave_cal_day_tue"),
    t("operations.leave_cal_day_wed"),
    t("operations.leave_cal_day_thu"),
    t("operations.leave_cal_day_fri"),
    t("operations.leave_cal_day_sat"),
  ];
  return (
    <article className="flex items-center gap-3 rounded-lg border border-border-subtle bg-surface-default p-3 text-sm">
      <Clock size={20} className="text-text-secondary" />
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <Link
            to={`/policies/shifts/${shift.id}`}
            className="font-semibold hover:underline"
          >
            {shift.name_en}
          </Link>
          <span className="rounded bg-surface-muted px-1.5 py-0.5 text-xs font-mono">{shift.code}</span>
          {!shift.is_active && (
            <span className="rounded bg-surface-muted px-1.5 py-0.5 text-xs text-text-secondary">{t("policies.shifts_inactive_pill")}</span>
          )}
        </div>
        <div className="mt-0.5 text-xs text-text-secondary">
          {t("policies.shifts_summary", {
            start: shift.start_time,
            end: shift.end_time,
            tz: shift.timezone,
            breakMin: shift.break_duration_minutes,
            graceMin: shift.grace_period_minutes,
          })}
        </div>
        <div className="mt-0.5 flex gap-1 text-xs text-text-tertiary">
          {days.map((d, i) => (
            <span
              key={d}
              className={
                shift.days_of_week[i]
                  ? "rounded bg-brand/10 px-1 py-0.5 text-brand"
                  : "rounded bg-surface-muted px-1 py-0.5 text-text-tertiary"
              }
            >
              {d}
            </span>
          ))}
        </div>
      </div>
      {shift.is_active ? (
        <button
          type="button"
          onClick={() => deactivateM.mutate()}
          disabled={deactivateM.isPending}
          className="rounded border border-border-default px-2 py-1 text-xs hover:bg-surface-muted disabled:opacity-50"
        >
          {t("policies.allowances_deactivate")}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => deleteM.mutate()}
          disabled={deleteM.isPending}
          className="rounded border border-rose-300 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50 disabled:opacity-50"
        >
          {t("common.delete")}
        </button>
      )}
    </article>
  );
}

export function ShiftsCatalogPage() {
  const { t } = useTranslation();
  const { legalEntityId: le, gate } = useLegalEntityGate();
  const [showNew, setShowNew] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["shifts", le, includeInactive],
    queryFn: () => api.listShifts(le, includeInactive),
    enabled: !!le,
  });
  if (gate) return gate;
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorPanel message={(q.error as Error).message} />;
  const refresh = () => qc.invalidateQueries({ queryKey: ["shifts", le] });
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("nav.shifts")}
        breadcrumbs={[
          { label: t("nav.policies"), href: "/policies" },
          { label: t("nav.shifts") },
        ]}
        actions={
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1 text-xs text-text-secondary">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
              {t("common.show_inactive")}
            </label>
            <button
              type="button"
              onClick={() => setShowNew(true)}
              className="flex items-center gap-1 rounded-sm bg-brand px-3 py-2 text-sm font-medium text-brand-fg hover:bg-brand-hover"
            >
              <Plus size={14} /> {t("common.new")}
            </button>
          </div>
        }
      />
      {(q.data ?? []).length === 0 ? (
        <EmptyState
          icon={Clock}
          headline={t("policies.shifts_empty_headline")}
          description={t("policies.shifts_empty_body")}
        />
      ) : (
        <div className="flex flex-col gap-2">
          {(q.data ?? []).map((s) => (
            <ShiftRow key={s.id} shift={s} onChanged={refresh} />
          ))}
        </div>
      )}
      {showNew && (
        <NewShiftDialog
          legalEntityId={le}
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}
